/**
 * WebApplicationOverviewTab Component
 *
 * Overview tab for web-application seeds. A webapp seed and its Assets-table
 * row are the SAME Neo4j node (`Seed` is just a label appended when
 * `Source == SeedSource`), so this tab renders the exact same `AssetOverview`
 * the asset drawer renders, fed by the same asset-side data hooks.
 *
 * This wrapper exists so the asset data hooks are called unconditionally in
 * their own component (hooks must not be conditional in SeedDrawerContent's
 * routing switch).
 */

import * as psl from 'psl';

import { Skeleton } from '@/components/ui/skeleton';
import { AssetOverview } from '@/sections/asset/components/overview';
import { buildOverviewAttributes } from '@/sections/asset/components/overview/buildOverviewAttributes';
import { isCloudResource } from '@/sections/asset/components/tabs';
import {
  useAssetAttributeProcessing,
  useAssetDrawerData,
} from '@/sections/asset/hooks/drawer';
import { RiskSeverity, Seed } from '@/types';
import { Regex } from '@/utils/regex.util';

interface WebApplicationOverviewTabProps {
  /** The web-application seed (its key doubles as the asset key) */
  seed: Seed;
  /** Seed drawer tab change handler (asset tab names are mapped to seed ones) */
  onTabChange?: (tab: string) => void;
}

export function WebApplicationOverviewTab({
  seed,
  onTabChange,
}: WebApplicationOverviewTabProps) {
  // Hooks run unconditionally, before any early return.
  const {
    assetAttributes,
    riskCounts,
    technologiesTree,
    asset,
    entityGroup,
    entityIdentifier,
    isInitialLoading,
  } = useAssetDrawerData(seed.key);

  const { attributes } = useAssetAttributeProcessing(
    assetAttributes,
    technologiesTree
  );

  if (isInitialLoading) {
    return (
      <Skeleton shape="block" className="m-6 min-h-48 w-[calc(100%-48px)]" />
    );
  }

  // Mirror AssetDrawer's assetWithRisk: every severity present, defaulting to 0
  const assetWithRisk = {
    ...asset,
    riskSummary: {
      [RiskSeverity.Critical]: riskCounts?.[RiskSeverity.Critical] || 0,
      [RiskSeverity.High]: riskCounts?.[RiskSeverity.High] || 0,
      [RiskSeverity.Medium]: riskCounts?.[RiskSeverity.Medium] || 0,
      [RiskSeverity.Low]: riskCounts?.[RiskSeverity.Low] || 0,
      [RiskSeverity.Info]: riskCounts?.[RiskSeverity.Info] || 0,
      [RiskSeverity.Exposure]: riskCounts?.[RiskSeverity.Exposure] || 0,
    },
  };

  const isAssetIdentifierAnIpAddress =
    Regex.IPV4.test(entityIdentifier || '') ||
    Regex.IPV6.test(entityIdentifier || '');
  const isAssetIdentifierACloudResource = isCloudResource(asset);

  // The SAME builder AssetDrawerContent uses — see buildOverviewAttributes
  // for why the cloud spreads (which can fire for a webapp seed via cloud
  // DNS patterns, not just key prefixes) must match the asset drawer's.
  const attributesByName = buildOverviewAttributes({
    asset,
    ptr: attributes.ptr || [],
    entityIdentifier,
    isIpAddress: isAssetIdentifierAnIpAddress,
  });

  // A webapp seed's `group` is a full URL, so psl.isValid is false and domain
  // stays undefined — kept for parity with the asset drawer's overview.
  const domain =
    entityGroup && psl.isValid(entityGroup)
      ? {
          name: entityGroup,
          registrantOrg: attributes.registrant?.[0]?.value || '',
        }
      : undefined;

  // OriginationSection's Affiliation Validation "View Details" button calls
  // onTabChange('history'); if no handler were passed, its fallback writes the
  // `assetDrawerTab` URL param, which is wrong inside the seed drawer. So we
  // ALWAYS pass a handler when the seed drawer provides one, mapping the asset
  // drawer's 'history' target to the seed drawer's 'seedHistory' tab.
  const handleTabChange = onTabChange
    ? (tab: string) => onTabChange(tab === 'history' ? 'seedHistory' : tab)
    : undefined;

  return (
    <AssetOverview
      attributesByName={attributesByName}
      domain={domain}
      asset={assetWithRisk}
      riskCounts={riskCounts}
      isAssetIdentifierAnIpAddress={isAssetIdentifierAnIpAddress}
      isAssetIdentifierACloudResource={isAssetIdentifierACloudResource}
      onTabChange={handleTabChange}
    />
  );
}
